import React, { FocusEvent, FunctionComponent } from "react";
import { TaxCode } from "./taxCodesApi";
import { Trans } from "@lingui/macro";
import NumberFormat from "react-number-format";
import InputMask from "react-input-mask";
import { useField } from "formik";

export interface Props {
  taxCode: TaxCode;
  onChange: (value: string | boolean | number) => void;
}

export const TaxCodeField: FunctionComponent<Props> = ({ taxCode, onChange }) => {
  const [{ value: filledInValue }] = useField(`c${taxCode.code}`);
  const [value, setValue] = React.useState<string | boolean | number>(taxCode.type === "bool" ? true : taxCode.default);

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    requestAnimationFrame(() => {
      if (document.activeElement === e.target) {
        e.target.select();
      }
    });
  };

  React.useEffect(() => {
    if (filledInValue !== undefined) {
      setValue(filledInValue);
    }
  }, [filledInValue, setValue]);

  React.useEffect(() => {
    onChange(value);
  }, [onChange, value]);

  switch (taxCode.type) {
    case "bool":
      return (
        <>
          <input disabled type="checkbox" checked className="amount_bool" />
          <span id="amounttype">{taxCode.default === "yes" ? <Trans>Ja</Trans> : <Trans>Nee</Trans>}</span>
        </>
      );
    case "date":
      return (
        <InputMask
          mask="99/99/9999"
          autoFocus
          onFocus={handleFocus}
          onChange={(event) => setValue(event.target.value)}
          value={value as string}
        />
      );
    case "num":
      return (
        <NumberFormat
          value={value as string}
          onValueChange={(value) => setValue(value.formattedValue)}
          decimalScale={0}
          allowNegative={false}
          autoFocus
          onFocus={handleFocus}
        />
      );
    case "pct":
      return (
        <NumberFormat
          value={value as string}
          onValueChange={(value) => setValue(value.floatValue?.toFixed(2).toString() || "")}
          decimalScale={2}
          allowNegative={false}
          decimalSeparator={"."}
          allowedDecimalSeparators={[",", "."]}
          autoFocus
          onFocus={handleFocus}
          suffix={"%"}
        />
      );
    default:
      return (
        <NumberFormat
          value={value as string}
          onValueChange={(value) => setValue(value.floatValue?.toFixed(2).toString() || "")}
          decimalScale={2}
          allowNegative={false}
          decimalSeparator={"."}
          allowedDecimalSeparators={[",", "."]}
          autoFocus
          onFocus={handleFocus}
          suffix={"€"}
        />
      );
  }
};

TaxCodeField.displayName = "TaxCodeField";
